import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useApp } from "../app/AppContext";
import { AiRewrite } from "./AiRewrite";
import type { Energy, TaskInput } from "../lib/api";

const ENERGIES: { key: Energy; label: string }[] = [
  { key: "high", label: "⚡ Deep" },
  { key: "low", label: "○ Light" },
];

export function TaskModal({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
  const app = useApp();
  const navigate = useNavigate();
  const editing = app.tasks.find((t) => t.id === taskId);
  const linkedNotes = editing ? app.notes.filter((n) => n.taskId === editing.id) : [];

  function openNote(id?: string) {
    const target =
      id ?? (editing ? app.createNote({ taskId: editing.id, projectId: editing.projectId }) : undefined);
    navigate({ to: "/app/notes", search: target ? { note: target } : {} });
    onClose();
  }

  const [title, setTitle] = useState(editing?.title ?? "");
  const [projectId, setProjectId] = useState<string>(editing?.projectId ?? "");
  const [pri, setPri] = useState<number>(editing?.pri ?? 2);
  const [energy, setEnergy] = useState<Energy | "">(editing?.energy ?? "");
  const [estMin, setEstMin] = useState<number>(editing?.estMin ?? 30);
  const [doDate, setDoDate] = useState(editing?.doDate ?? "");
  const [deadline, setDeadline] = useState(editing?.deadline ?? "");

  function save() {
    const t = title.trim();
    if (!t) return;
    const patch: TaskInput = {
      title: t,
      projectId: projectId || null,
      pri,
      energy: energy || null,
      estMin: Number.isFinite(estMin) ? estMin : 30,
      doDate: doDate || null,
      deadline: deadline || null,
    };
    if (editing) app.updateTask(editing.id, patch);
    else app.createTask({ ...patch, title: t });
    onClose();
  }

  return (
    <div className="ovl" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{editing ? "Edit task" : "New task"}</h3>

        <div className="f">
          <label>Title</label>
          {/* airw-field: the Rewrite trigger sits inside the input; the suggestion
              flows below it, pushing the fields down instead of covering them. */}
          <div className="airw-field">
            <input
              className="inp"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
              }}
            />
            <AiRewrite text={title} onAccept={setTitle} affix />
          </div>
        </div>

        <div className="g2">
          <div className="f">
            <label>Project</label>
            <select className="selc" style={{ width: "100%" }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— none —</option>
              {app.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label>Energy</label>
            <div className="seg">
              {ENERGIES.map((en) => (
                <button
                  key={en.key}
                  className={energy === en.key ? "on" : ""}
                  onClick={() => setEnergy(energy === en.key ? "" : en.key)}
                  type="button"
                >
                  {en.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="g3">
          <div className="f">
            <label>Priority</label>
            <select className="selc" style={{ width: "100%" }} value={pri} onChange={(e) => setPri(Number(e.target.value))}>
              {[1, 2, 3, 4].map((p) => (
                <option key={p} value={p}>
                  P{p}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label>Est (min)</label>
            <input className="inp" style={{ width: "100%" }} type="number" min={0} step={5} value={estMin} onChange={(e) => setEstMin(Number(e.target.value))} />
          </div>
          <div className="f">
            <label>Do on</label>
            <input className="inp" style={{ width: "100%" }} type="date" value={doDate} onChange={(e) => setDoDate(e.target.value)} />
          </div>
        </div>

        <div className="f">
          <label>Hard deadline (only if real)</label>
          <input className="inp" style={{ width: "100%" }} type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </div>

        {editing && (
          <div className="f">
            <label>Notes</label>
            <div className="tm-notes">
              {linkedNotes.map((n) => (
                <button key={n.id} type="button" className="tm-note" onClick={() => openNote(n.id)}>
                  ↳ {n.title.trim() || "Untitled note"}
                </button>
              ))}
              <button type="button" className="tm-note add" onClick={() => openNote()}>
                + Add note
              </button>
            </div>
          </div>
        )}

        <div className="mact">
          {editing && (
            <button
              className="mbtn ghost"
              style={{ marginRight: "auto", color: "var(--danger)" }}
              onClick={() => {
                app.deleteTask(editing);
                onClose();
              }}
            >
              Delete
            </button>
          )}
          <button className="mbtn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="mbtn" disabled={!title.trim()} onClick={save}>
            {editing ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
